import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { AlertCircle, AlertTriangle, CheckCircle2, Package, Power, Wrench } from "lucide-react"
import { InstrumentsTable } from "../../components/InstrumentsTable"
import { buttonVariants } from "@/components/ui/button"
import { Link, useLocation } from "@tanstack/react-router"
import type { Instrument } from "@/Wist-blockchain/collections/instruments/types"
import { useEffect, useMemo, useState } from "react"
import { getInstrument } from "@/Wist-blockchain/collections/instruments/instruments.service"
import { databases } from "@/lib/appwrite"
import { Query } from "appwrite"
import { fetchCompanies } from "@/lib/collections/organizations/getOrganizationsFetch"

const DB_BLOCKCHAIN  = import.meta.env.VITE_BLOCKCHAIN_DATABASE_ID
const COL_REPORTS    = import.meta.env.VITE_BLOCKCHAIN_SERVICE_REPORT_COLLECTION_ID

const pct = (n: number, total: number) =>
    total === 0 ? "0%" : `${Math.round((n / total) * 100)}% del total`

const StatCard = ({
    title, value, sub, icon, color,
}: {
    title: string
    value: number
    sub: string
    icon: React.ReactNode
    color: string
}) => (
    <Card className="shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
            <div className={`rounded-lg p-2 ${color}`}>{icon}</div>
        </CardHeader>
        <CardContent>
            <div className="text-2xl font-bold">{value}</div>
            <p className="text-xs text-muted-foreground mt-1">{sub}</p>
        </CardContent>
    </Card>
)

const ListInstruments = () => {
    const { pathname } = useLocation()
    const [instruments, setInstruments] = useState<Instrument[]>([])
    const [brandMap, setBrandMap] = useState<Map<string, string>>(new Map())
    const [orgMap, setOrgMap] = useState<Map<string, string>>(new Map())
    const [maintenanceMap, setMaintenanceMap] = useState<Map<string, string>>(new Map())
    const [lastServiceMap, setLastServiceMap] = useState<Map<string, string>>(new Map())

    useEffect(() => {
        const fetchAll = async () => {
            const [instrumentsResult, brandsResult, orgsResult, reportsResult] = await Promise.all([
                getInstrument(),
                databases.listDocuments(
                    import.meta.env.VITE_BLOCKCHAIN_DATABASE_ID,
                    import.meta.env.VITE_BLOCKCHAIN_BRANDS,
                    [Query.select(['$id', 'name']), Query.limit(5000)]
                ),
                fetchCompanies(),
                databases.listDocuments(DB_BLOCKCHAIN, COL_REPORTS, [
                    Query.equal('status', 'final'),
                    Query.orderDesc('$updatedAt'),
                    Query.limit(1000),
                ]).catch(() => ({ documents: [] })),
            ])
            setInstruments(instrumentsResult.documents)
            setBrandMap(new Map(brandsResult.documents.map(d => [d.$id, d.name as string])))
            setOrgMap(new Map(orgsResult.map(c => [c.$id, c.commercialName || c.name])))

            const nextMaintenanceMap = new Map<string, string>()
            const latestServiceMap   = new Map<string, string>()

            for (const report of reportsResult.documents) {
                const instrumentId = report.idInstrument as string
                if (!instrumentId) continue
                if (!latestServiceMap.has(instrumentId) && report.serviceDate) {
                    latestServiceMap.set(instrumentId, report.serviceDate as string)
                }
                if (!nextMaintenanceMap.has(instrumentId) && report.nextQualificationDate) {
                    nextMaintenanceMap.set(instrumentId, report.nextQualificationDate as string)
                }
            }

            setMaintenanceMap(nextMaintenanceMap)
            setLastServiceMap(latestServiceMap)
        }
        fetchAll()
    }, [pathname])

    const stats = useMemo(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const total       = instruments.length
        const active             = instruments.filter(i => i.status === "operativo").length
        const withObservations   = instruments.filter(i => i.status === "operativo_observaciones").length
        const inactive           = instruments.filter(i => i.status === "fuera_servicio").length
        const overdueCalibration = instruments.filter(i => {
            const dateStr = maintenanceMap.get(i.$id)
            if (!dateStr) return false
            const date = new Date(`${dateStr.split("T")[0]}T12:00:00`)
            return date < today
        }).length
        return { total, active, withObservations, inactive, overdueCalibration }
    }, [instruments, maintenanceMap])

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <Package className="h-4 w-4" />
                        <BreadcrumbPage>Lista de instrumentos</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="space-y-6 pt-6">
                {/* Stats */}
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                    <StatCard
                        title="Operativos"
                        value={stats.active}
                        sub={pct(stats.active, stats.total)}
                        color="bg-emerald-500/10"
                        icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                    />
                    <StatCard
                        title="Con observaciones"
                        value={stats.withObservations}
                        sub={pct(stats.withObservations, stats.total)}
                        color="bg-amber-500/10"
                        icon={<Wrench className="h-4 w-4 text-amber-500" />}
                    />
                    <StatCard
                        title="Fuera de servicio"
                        value={stats.inactive}
                        sub={pct(stats.inactive, stats.total)}
                        color="bg-red-500/10"
                        icon={<Power className="h-4 w-4 text-red-500" />}
                    />
                    <StatCard
                        title="Calificación vencida"
                        value={stats.overdueCalibration}
                        sub={stats.overdueCalibration === 0 ? "Todo al día" : "Requieren atención"}
                        color={stats.overdueCalibration > 0 ? "bg-red-500/10" : "bg-emerald-500/10"}
                        icon={<AlertTriangle className={`h-4 w-4 ${stats.overdueCalibration > 0 ? "text-red-500" : "text-emerald-500"}`} />}
                    />
                </div>

                {/* Sin estado (sin clasificar) */}
                {instruments.filter(i => !i.status).length > 0 && (
                    <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-400">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span>
                            <span className="font-semibold">{instruments.filter(i => !i.status).length}</span> equipo(s) sin estado asignado.
                        </span>
                    </div>
                )}

                {/* Header tabla */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <h2 className="text-2xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                        Gestión de instrumentos
                    </h2>
                    <Link to="/instruments/addInstrument" className={buttonVariants({ variant: "default" })}>
                        Agregar equipo
                    </Link>
                </div>

                <InstrumentsTable instruments={instruments} brandMap={brandMap} orgMap={orgMap} maintenanceMap={maintenanceMap} lastServiceMap={lastServiceMap} />
            </div>
        </>
    )
}

export default ListInstruments
